import moment from 'moment';
import api from '@core/services/api/_api';
import ENUMS from '@core/classes/Enums';
import $modal from '@core/services/modal';
import modalInfo from '@core/modals/modalInfo';
import util from '@core/services/util';

const CBCHelper = {
	/**
	 * @param {object} customer
	 * @returns CBC report using customer id from customer application
	 */
	async getCBCReportByCustomerId(id) {
		if (!id) return null;

		let response = await api.cbc.getReportsByCustomerID(id);

		if (response.status == 200) return response;
		else console.log('Error retrieving cbc report with app id');
	},

	/**
	 *
	 * @param {string} id
	 * @param {string} dealId
	 * @returns CBC Credit Report
	 */
	async getCreditReport(id = null, dealId = null) {
		try {
			let response = await api.creditreport.getByCustomerId(id, dealId);
			if (response.status == 200) {
				return response;
			}
		} catch (error) {
			console.error('Error fetching credit report:', error);
		}
	},

	/**
	 *
	 * @param {Date} date
	 * @returns Boolean, if its within 30 days it is true
	 */
	isWithin30Days(date) {
		let lastModified = util.toMoment(date).utc(true);
		let timeDifference = moment().diff(lastModified, 'days');
		return timeDifference < 30;
	},

	/**
	 * @param {object} store
	 * @returns {boolean} Checks if the store has active CBC Credentials based on store partner codes.
	 */
	isEnrolledWithCBC(store) {
		return store.partnerCodes?.find(data => data.type === ENUMS.PARTNER_CODES.CBC_CUSTOMERID);
	},

	/**
	 * @param {object} store
	 * @returns {boolean} Checks if the store has CBC enabled (the select box on store settings).
	 */
	isCBCEnabled(store) {
		return store?.storeSettings?.isCBCEnabled ?? false;
	},

	// USER CREDS SECTION //

	/**
	 * @param {object} store
	 * @param {object} codeList
	 * @param {class} cbcSettings
	 * @returns gets the global credentials saved from key value index.
	 */
	async getUserCredentials(storeCode, cbcSettings) {
		const response = await api.cbc.getUserCreds(storeCode);
		if (response && response.data) {
			cbcSettings.credentials.customerID = cbcSettings.credentials.customerID ?? response.data.credentials.customerID;
			cbcSettings.credentials.masterID = cbcSettings.credentials.masterID ?? response.data.credentials.masterID;
			cbcSettings.credentials.masterPassword = response.data.credentials.masterPassword;
			cbcSettings.credentials.userID = cbcSettings.credentials.userID ?? response.data.credentials.userID;
			cbcSettings.credentials.userPassword = response.data.credentials.userPassword;
			cbcSettings.credentials.masterPasswordLastUpdated = response.data.credentials.masterPasswordLastUpdated;
			cbcSettings.credentials.userPasswordLastUpdated = response.data.credentials.userPasswordLastUpdated;
			return response;
		}
	},

	/**
	 * @param {object} store
	 * @param {object} codeList
	 * @param {class} cbcSettings
	 * @returns Global credentials new master PW
	 */
	async generateMasterPassword(store, codeList, cbcSettings) {
		let response = await api.cbc.resetMasterPassword(cbcSettings);
		if (response.data.success) {
			cbcSettings.credentials.masterPassword = response.data.credentials.masterPassword;
			cbcSettings.credentials.masterPasswordLastUpdated = response.data.credentials.masterPasswordLastUpdated;
			return true;
		}
	},

	/**
	 * @param {object} store
	 * @param {object} codeList
	 * @param {class} cbcSettings
	 * @returns a new global user password
	 */
	async generateUserPassword(store, codeList, cbcSettings) {
		let response = await api.cbc.resetUserPassword(cbcSettings);
		if (response && response.data) {
			if (response.data.success) {
				cbcSettings.credentials.userPassword = response.data.credentials.userPassword;
				cbcSettings.credentials.masterPasswordLastUpdated = response.data.credentials.userPasswordLastUpdated;
				return true;
			}
		}
	},

	/**
	 *
	 * @param {integer} type
	 * @param {string} code
	 * @param {object} store
	 * @param {object} codeList
	 * @function updates the partner code for the store's values. passwords are no longer stored in a partner code.
	 */
	updatePartnerCode(type, code, store, codeList) {
		if (code) {
			let partnerCode = store.partnerCodes.find(pc => pc.type === type);

			if (partnerCode) {
				partnerCode.code = code;
			} else {
				store.partnerCodes.push({ type, code });
				codeList.push(ENUMS.PARTNER_CODES_TYPES.find(pc => pc.type === type));
			}
		} else {
			// this removes a partner code
			store.partnerCodes = store.partnerCodes.filter(pc => pc.type !== type);
			codeList = codeList.filter(c => c.type !== type);
		}
	},

	/**
	 * @param {object} store
	 * @param {object} codeList
	 * @param {class} cbcSettings
	 * @function Installs the CBC creds for global
	 */
	async installCBC(store, codeList, cbcSettings) {
		let response = await api.cbc.addUser(cbcSettings);
		if (response && response.data) {
			if (response.data.success) {
				cbcSettings.credentials.userID = response.data.credentials.userID;
				cbcSettings.credentials.userPassword = response.data.credentials.userPassword;
				cbcSettings.credentials.masterID = response.data.credentials.masterID;
				cbcSettings.credentials.masterPassword = response.data.credentials.masterPassword;
				return true;
			}
		}
	},

	/**
	 * @param {object} store
	 * @param {object} codeList
	 * @param {class} cbcSettings
	 * @function Removes the user creds for the globals. just makes the flag disabled, does not delete.
	 */
	async uninstallCBC(store, codeList, cbcSettings) {
		let response = await api.cbc.deactivate(cbcSettings);
		if (response && response.data) {
			cbcSettings.credentials.customerID = null;
			cbcSettings.credentials.masterID = null;
			cbcSettings.credentials.masterPassword = null;
			cbcSettings.credentials.userID = null;
			cbcSettings.credentials.userPassword = null;
			return true;
		}
	},

	/**
	 * @param {object} store
	 * @param {object} codeList
	 * @param {class} cbcSettings
	 * @method opens the confirmation modal
	 */
	openConfirmMasterPassword(store, codeList, cbcSettings) {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				title: 'Confirm',
				info: 'Are you sure you want to reset the master password ?',
				acceptText: 'Continue',
				cancelText: 'Cancel',
			},
			backdrop: true,
			postFunction: () => this.generateMasterPassword(store, codeList, cbcSettings),
		});
	},
	/**
	 * @param {object} customer
	 * @param {string} type
	 * @param {string} addressType
	 * @method opens create a customerApplicant for cbcRequests.
	 */
	createCustomerApplicant(customer, type = 'primary', addressType = 'current') {
		return {
			Type: type,
			Person_name: {
				First_name: customer.firstName,
				Middle_name: customer.middleName,
				Last_name: customer.lastName,
				Generation: customer.suffix,
			},
			Address_data: {
				Address: {
					Type: addressType,
					Line_one: customer.address + (customer.addressExtra != null ? ` ${customer.addressExtra}` : ''),
					City: customer.city,
					State_or_province: customer.state,
					Postal_code: customer.zip,
				},
			},
			Social: customer.ssnHashed,
			Birthdate: customer.dob,
		};
	},
	/**
	 * @param {FIMenuCustomer} customer
	 * @param {object} store
	 * @param {number} reportType
	 * @param {string} selectedCreditPullVersion
	 * @param {boolean} automaticPull
	 * @method Makes a credit pull and returns a creditReport based on the selected credit pull version.
	 */

	async pullCredit(customer, store, reportType, selectedCreditPullVersion = null, automaticPull = false, DealId = null, pin = '') {
		if (CBCHelper.isCBCEnabled(store)) {
			var applicantArray = [CBCHelper.createCustomerApplicant(customer)];

			let request = {
				PrimaryCustomerID: customer.id,
				StoreCode: store.storeCode,
				ReportType: reportType,
				ApplicantArray: applicantArray,
				PIN: pin,
				IsJoint: false,
				IsHardPull: false,
				DealId: DealId,
				CreditPullVersion: selectedCreditPullVersion,
				AutomaticPull: automaticPull,
			};
			try {
				let response = null;

				if (reportType == ENUMS.CBC_REPORT_TYPES.OFAC) {
					// Ofac Report pull
					response = await api.cbc.getOfacReport(request);
				} else {
					// Bureau credit report
					response = await api.cbc.creditPull(request);
				}
				return response.data;
			} catch (err) {
				console.log('Error', err);
				return null;
			}
		}
		return null;
	},
	/**
	 * @param {flags} List of all the flags
	 * @param {object} store
	 * @param {string} reportType
	 * @method opens Makes a credit pull and returns a creditReport.
	 */
	anyRedflags(flags) {
		return flags?.some(flag => flag.redflags?.some(redflag => !redflag.comment));
	},

	/**
	 *
	 * @param {string} reportType
	 * @param {object} customer
	 * @returns filtered credit pulls by report type
	 */
	getCreditPullsByReportType(reportType, customer) {
		let bureauType = ENUMS.CBC_REPORT_TYPES.reportTypeEnumToString(reportType);
		let creditPulls = customer.creditPulls;
		let filter = creditPulls.filter(cp => {
			return cp.request.header_data.action == bureauType;
		});
		return filter;
	},

	async saveCreditPullToCreditReport(physicalReportId, bureauType, customerId) {
		// we'll send the ID of the credit pull and the bureau type.
		try {
			let response = await api.cbc.saveCreditPullToCreditReport(physicalReportId, bureauType, customerId);
			if (response?.status == 200) {
				return response;
			}
		} catch (error) {
			console.log('Error', error);
			return;
		}
	},
};

export default CBCHelper;
